/**
 * @module use-cases/slice/catalog
 * @desc Catalog Slice (Reducers and ExtraReducers)
 */

import type { Catalog } from 'core/domains';
import { createSlice, isAnyOf } from 'core/use-cases/utilities';
import { Utilities } from 'core/domains';

import * as actions from './actions';
import { initialState } from './initial-state';
import { AVAILABLE_OPTION } from 'core/domains/catalog.d';

const catalogSlice = createSlice({
  name: 'catalog',
  initialState,
  reducers: {},

  extraReducers: (builder) => {
    builder.addCase(actions.catalogCleanUp, (state) => {
      state.data = [];
      state.message = '';
      state.isRunning = true;
      state.queryOptions = {};
      state.status = Utilities.STATUS.IDLE;
      state.updatedAt = 0;

      state.pagination = {
        data: {} as Catalog['pagination']['data'],
        message: '',
        isRunning: false,
        status: Utilities.STATUS.IDLE,
        updatedAt: 0,
      };
    });

    builder.addCase(actions.catalogFetch.request, (state, payload) => {
      state.message = '';
      state.notFound = false;
      state.isRunning = true;
      state.status = Utilities.STATUS.RUNNING;
      state.updatedAt = 0;
      state.queryOptions.query = payload.payload?.query;
    });

    builder.addCase(actions.searchPreviousFetch.request, (state) => {
      state.message = '';
      state.isRunning = false;
      state.isRunningPreview = true;
      state.statusPreview = Utilities.STATUS.RUNNING;
      state.updatedAt = 0;
    });

    builder.addCase(actions.catalogFetch.success, (state, { meta, payload }) => {
      const {
        data,
        pagination: { data: paginationData },
      } = payload;

      state.data = data;
      state.message = '';
      state.notFound = !data.length;
      state.isRunning = false;
      state.status = Utilities.STATUS.SUCCESS;
      state.updatedAt = meta.updatedAt;
      state.queryOptions = { ...(meta.payload || {}), page: paginationData.currentPage };

      state.pagination = {
        data: paginationData,
        message: '',
        isRunning: false,
        status: Utilities.STATUS.SUCCESS,
        updatedAt: meta.updatedAt,
      };
    });

    builder.addCase(actions.searchPreviousFetch.success, (state, { meta, payload }) => {
      const {
        data,
        pagination: { data: paginationData },
      } = payload;

      state.previewSearch = data;
      state.message = '';
      state.isRunning = false;
      state.isRunningPreview = false;
      state.status = Utilities.STATUS.SUCCESS;
      state.statusPreview = Utilities.STATUS.SUCCESS;
      state.updatedAt = meta.updatedAt;

      state.pagination = {
        data: paginationData,
        message: '',
        isRunning: false,
        status: Utilities.STATUS.SUCCESS,
        updatedAt: meta.updatedAt,
      };
    });

    builder.addCase(actions.catalogNextPageFetch.request, (state) => {
      state.pagination = {
        data: state.pagination.data,
        isRunning: true,
        message: '',
        status: Utilities.STATUS.RUNNING,
        updatedAt: 0,
      };
    });

    builder.addCase(actions.searchEnter, (state, { payload }) => {
      state.data = [];
      state.queryOptions.query = payload;
      state.queryOptions.availability = AVAILABLE_OPTION.first;
      state.queryOptions.itemsPerPage = 16;
      state.status = Utilities.STATUS.RUNNING;

      if (state.statusPreview === Utilities.STATUS.ERROR) {
        state.status = Utilities.STATUS.ERROR;
      }

      if (state.previewSearch.length === 0 && state.statusPreview === Utilities.STATUS.SUCCESS) {
        state.notFound = true;
        state.status = Utilities.STATUS.SUCCESS;
      } else {
        state.notFound = false;
      }
    });

    builder.addCase(actions.searchEnterRedirect, (state) => {
      state.data = state.previewSearch;
      state.status = Utilities.STATUS.SUCCESS;

      if (state.previewSearch.length === 0 && state.statusPreview === Utilities.STATUS.SUCCESS) {
        state.notFound = true;
      }
    });

    builder.addCase(actions.catalogNextPageFetch.success, (state, { meta, payload }) => {
      const {
        data,
        pagination: { data: paginationData },
      } = payload;

      state.data = state.data.concat(data);
      state.queryOptions = { ...(meta.payload || {}), page: paginationData.currentPage };

      state.pagination = {
        data: paginationData,
        isRunning: false,
        message: '',
        status: Utilities.STATUS.SUCCESS,
        updatedAt: meta.updatedAt,
      };
    });

    builder.addCase(actions.catalogNextPageFetch.failure, (state, { error }) => {
      const { message } = error;

      state.pagination = {
        data: state.pagination.data,
        isRunning: false,
        message,
        status: Utilities.STATUS.ERROR,
      };
    });

    builder.addCase(actions.getResellerAvailableCatalogs.success, (state, { payload }) => {
      state.cycleGuides = payload;
    });

    builder.addMatcher(
      isAnyOf(actions.catalogFetch.failure, actions.searchPreviousFetch.failure),
      (state, { error }) => {
        const { message } = error;

        state.message = message;
        state.notFound = false;
        state.isRunning = false;
        state.isRunningPreview = false;
        state.status = Utilities.STATUS.ERROR;
        state.statusPreview = Utilities.STATUS.ERROR;
      },
    );
  },
});

export const { reducer } = catalogSlice;
export { actions };
